import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wordmark } from "./ui.jsx";
import Icon from "./icons.jsx";
import LocaleSwitcher from "./LocaleSwitcher.jsx";
import { useLocaleNav, useCurrentLocale } from "../lib/useLocaleNav.js";
import { goToSignup, goToLogin } from "../lib/handoff.js";

export default function Nav({ overDark = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const go = useLocaleNav();
  const locale = useCurrentLocale();

  const route = (() => {
    const seg = location.pathname.split("/").filter(Boolean);
    return seg[1] || "home";
  })();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { id: "home",      label: t("nav.home"),     target: "home" },
    { id: "how",       label: t("nav.how"),      target: "how" },
    { id: "results",   label: t("nav.results"),  target: "results" },
    { id: "pricing",   label: t("nav.pricing"),  target: "pricing" },
    { id: "fair-hospitality", label: t("nav.fairHospitality"), target: "fair-hospitality" },
    { id: "contact",   label: t("nav.contact"),  target: "contact" }
  ];

  const inverse = overDark && !scrolled;
  const linkColor = inverse ? "rgba(255,255,255,.82)" : "var(--ink-2)";
  const linkColorActive = inverse ? "#fff" : "var(--persimmon)";
  const navBorder = scrolled ? "var(--line)" : "transparent";
  const navBg = scrolled ? "color-mix(in oklab, var(--bg) 84%, transparent)" : "transparent";

  const handleSignup = (e) => {
    e.preventDefault();
    goToSignup({ locale });
  };

  const handleLogin = (e) => {
    e.preventDefault();
    goToLogin();
  };

  return (
    <header style={{
      position: overDark ? "fixed" : "sticky",
      top: 0, left: 0, right: 0, zIndex: 50,
      backdropFilter: scrolled ? "blur(14px) saturate(150%)" : "none",
      WebkitBackdropFilter: scrolled ? "blur(14px) saturate(150%)" : "none",
      background: navBg,
      borderBottom: "1px solid " + navBorder,
      transition: "background .25s ease, border-color .25s ease"
    }}>
      <div className="container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px", gap: 24 }}>
        <Wordmark size={22} variant={inverse ? "reverse" : "default"} />

        <nav className="nav-hide-narrow" style={{ display: "flex", alignItems: "center", gap: 0 }}>
          {links.map(l => (
            <a key={l.id}
               href={l.target === "home" ? `/${locale}` : `/${locale}/${l.target}`}
               onClick={(e) => { e.preventDefault(); go(l.target); }}
               style={{
                 padding: "10px 12px",
                 fontSize: 14,
                 fontWeight: route === l.target ? 600 : 500,
                 color: route === l.target ? linkColorActive : linkColor,
                 textDecoration: "none",
                 fontFamily: "Inter, sans-serif",
                 transition: "color .15s ease",
                 whiteSpace: "nowrap"
               }}>{l.label}</a>
          ))}
        </nav>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="nav-hide-narrow"><LocaleSwitcher inverse={inverse} /></span>
          <a href="#" onClick={handleLogin}
             className={"btn btn-sm nav-hide-narrow " + (inverse ? "" : "btn-ghost")}
             style={inverse ? { background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,.32)" } : undefined}>
            {t("nav.login")}
          </a>
          {/* Get Started button hidden for now */}
          {false && (
            <a href="#" onClick={handleSignup} className="btn btn-primary btn-sm nav-hide-narrow">
              {t("nav.getStarted")}
            </a>
          )}
          <button className="nav-show-narrow" onClick={() => setOpen(o => !o)} aria-label={t("nav.menu")} style={{
            all: "unset", cursor: "pointer", width: 42, height: 42, borderRadius: 999,
            border: "1px solid " + (inverse ? "rgba(255,255,255,.32)" : "var(--line-2)"),
            color: inverse ? "#fff" : "var(--ink)",
            display: "inline-flex", alignItems: "center", justifyContent: "center"
          }}>
            <Icon name={open ? "close" : "menu"} size={18} />
          </button>
        </div>
      </div>

      {open && (
        <div className="nav-show-narrow" style={{
          borderTop: "1px solid var(--line)", background: "var(--bg)",
          padding: "8px 24px 28px", display: "flex", flexDirection: "column"
        }}>
          {links.map(l => (
            <a key={l.id}
               href={l.target === "home" ? `/${locale}` : `/${locale}/${l.target}`}
               onClick={(e) => { e.preventDefault(); go(l.target); }}
               style={{
                 padding: "18px 4px", borderBottom: "1px solid var(--line)",
                 fontSize: 20, color: "var(--ink)", textDecoration: "none",
                 fontFamily: "Satoshi, sans-serif", fontWeight: 600
               }}>{l.label}</a>
          ))}
          <div style={{ marginTop: 16 }}><LocaleSwitcher /></div>
          <a href="#" className="btn btn-ghost" style={{ marginTop: 16 }} onClick={handleLogin}>{t("nav.login")}</a>
          {/* Get Started button hidden for now */}
          {false && (
            <a href="#" className="btn btn-primary" style={{ marginTop: 12 }} onClick={handleSignup}>{t("nav.getStarted")}</a>
          )}
        </div>
      )}
    </header>
  );
}
